class QuickAdd extends HTMLElement {
  #TEMPLATE = '?view=quickadd';
  #MOBILE_BREAKPOINT = 1025;

  constructor() {
    super();
    this.fetchProductDataFunction = this.fetchProductData.bind(this);
    this.handleLoadEventFunction = this.handleLoadEvent.bind(this);
    this.handleFormCloseEventFunction = this.handleFormCloseEvent.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.fetchProductDataFunction);
    document.addEventListener('quickaddFetchingProductData', this.handleLoadEventFunction);
    document.addEventListener('quickaddFormClose', this.handleFormCloseEventFunction);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.fetchProductDataFunction);
    document.removeEventListener('quickaddFetchingProductData', this.handleLoadEventFunction);
    document.removeEventListener('quickaddFormClose', this.handleFormCloseEventFunction);
  }

  fetchProductData(e) {
    e.preventDefault();
    this.setLoading(true);

    const isMobile = window.innerWidth < this.#MOBILE_BREAKPOINT;
    const isInsideLookbookModal = this.closest('#lookbook_modal') !== null;

    const url = this.dataset.quickadd?.split('?')[0];
    if (!url) {
      console.error('QuickAdd URL is not defined or invalid.');
      this.setLoading(false);
      return;
    }

    fetch(url + this.#TEMPLATE)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch product data.');
        return res.text();
      })
      .then(html => {
        const parser = new DOMParser();
        const parsedHtml = parser.parseFromString(html, 'text/html');
        const productForm = parsedHtml.querySelector('form');
        if (!productForm) throw new Error('Product form not found in response.');

        const quickaddFormElement = document.createElement('quick-add-form');
        quickaddFormElement.classList.add('hide-animation');
        quickaddFormElement.appendChild(productForm);

        if (isMobile && isInsideLookbookModal) {
          const placeholder = document.getElementById('quick-add-placeholder');
          if (placeholder) {
            placeholder.appendChild(quickaddFormElement);
          } else {
            console.error('QuickAdd placeholder not found for mobile.');
          }
        } else {
          this.insertAdjacentElement('afterend', quickaddFormElement);
        }

        this.classList.add('hide-desktop');
      })
      .catch(err => {
        console.error('Error fetching product data:', err);
      })
      .finally(() => {        
        this.setLoading(false);
      });
  }

  setLoading(isLoading) {
    this.classList.toggle('loading', isLoading);
    const loadEvent = new CustomEvent('quickaddFetchingProductData', { detail: isLoading });
    document.dispatchEvent(loadEvent);
  }

  handleLoadEvent({ detail: isLoading }) {
    const buttons = this.querySelectorAll('button');
    buttons.forEach(button => {
      if (isLoading) {
        button.setAttribute('disabled', '');
      } else {
        button.removeAttribute('disabled');
      }
    });
  }

  handleFormCloseEvent() {
    this.classList.remove('hide-desktop');
  }
}

class QuickAddForm extends HTMLElement {
  #MOBILE_BREAKPOINT = 1024;

  constructor() {
    super();
    this.clickOutsideFunction = this.handleClickOutside.bind(this);
  }

  connectedCallback() {
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-live', 'polite');
    document.addEventListener('click', this.clickOutsideFunction);

    setTimeout(() => this.classList.remove('hide-animation'), 10);

    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('keydown', this.formNavigation.bind(this));
      form.addEventListener('change', this.formSubmitOnChange.bind(this));
    }

    const closeButton = this.querySelector('[data-quickadd-close]');
    if (closeButton) {
      closeButton.addEventListener('click', this.removeSelf.bind(this));
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.clickOutsideFunction);
  }

  formNavigation(e) {
    if (e.target.nodeName === 'INPUT' && e.key.includes('Arrow')) {
      e.preventDefault();
      const inputs = this.querySelectorAll('.quick-add-form__option input');
      if (!inputs.length) return;

      const firstInput = inputs[0];
      const lastInput = inputs[inputs.length - 1];
      const currentIndex = [...inputs].indexOf(e.target);

      const focusNextSibling = () => inputs[(currentIndex + 1) % inputs.length]?.focus();
      const focusPrevSibling = () => inputs[(currentIndex - 1 + inputs.length) % inputs.length]?.focus();

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          focusNextSibling();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          focusPrevSibling();
          break;
      }
    }
  }

  formSubmitOnChange(e) {
    const formData = new FormData(e.currentTarget);
    const selectedId = formData.get('id');
    const optionElement = e.currentTarget.querySelector(`[value="${selectedId}"]`);

    if (optionElement?.parentElement?.classList.contains('disabled')) {
      console.warn('Selected variant is out of stock.');
      return;
    }

    this.setLoading(true);

    fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      body: formData
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to add product to cart.');
        if (window.location.pathname === '/cart') {
          window.location.reload();
        } else {
          miniCart?.refresh?.();
          this.removeSelf();
        }
      })
      .catch(err => {
        console.error('Error adding product to cart:', err);
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  removeSelf() {
    const closeEvent = new CustomEvent('quickaddFormClose');
    document.dispatchEvent(closeEvent);

    if (window.innerWidth < this.#MOBILE_BREAKPOINT) {
      this.addEventListener('transitionend', () => {
        this.remove();
      }, { once: true });
      this.classList.add('hide-animation');
    } else {
      this.remove();
    }
  }

  handleClickOutside(e) {
    if (!e.composedPath().includes(this)) {
      this.removeSelf();
    }
  }

  setLoading(isLoading) {
    this.classList.toggle('loading', isLoading);
  }
}

customElements.define('quick-add', QuickAdd);
customElements.define('quick-add-form', QuickAddForm);